import React, { useState, useEffect } from "react";
import {
  MessageSquare,
  Zap,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  Sparkles,
  Bot,
  Shield,
  Brain,
  Cpu,
  Network,
} from "lucide-react";

export default function MainPreview() {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [displayedMessages, setDisplayedMessages] = useState([]);
  const [isRestarting, setIsRestarting] = useState(false);

  const messages = [
    {
      id: 1,
      agent: "마케팅 AI",
      icon: TrendingUp,
      content:
        "타겟 고객층 분석을 완료했습니다. 2030 세대 중 건강한 라이프스타일에 관심 있는 도시 거주자들이 주요 타겟입니다.",
      round: 1,
      sentiment: "positive",
      confidence: 0.85,
      gradient: "from-blue-500 to-indigo-600",
    },
    {
      id: 2,
      agent: "기술 분석 AI",
      icon: Cpu,
      content:
        "IoT 센서 기반 자동 급수 시스템과 LED 조명 제어를 통해 식물 성장을 최적화할 수 있습니다. 앱으로 원격 모니터링도 가능합니다.",
      round: 1,
      sentiment: "positive",
      confidence: 0.92,
      gradient: "from-purple-500 to-pink-600",
    },
    {
      id: 3,
      agent: "재무 분석 AI",
      icon: TrendingDown,
      content:
        "초기 투자 비용이 예상보다 높게 산출되었습니다. 하드웨어 개발비를 재검토해야 할 것 같습니다.",
      round: 2,
      sentiment: "negative",
      confidence: 0.78,
      gradient: "from-red-500 to-orange-600",
    },
    {
      id: 4,
      agent: "리스크 관리 AI",
      icon: Shield,
      content:
        "공급망 불안정성과 원자재 가격 변동이 주요 리스크 요인입니다. 대안 공급업체 확보가 필요합니다.",
      round: 2,
      sentiment: "neutral",
      confidence: 0.81,
      gradient: "from-gray-500 to-gray-600",
    },
    {
      id: 5,
      agent: "제품 설계 AI",
      icon: Brain,
      content:
        "모듈형 설계를 통해 제조 비용을 20% 절감할 수 있는 방안을 찾았습니다. 품질은 유지하면서 효율성을 높일 수 있습니다.",
      round: 3,
      sentiment: "positive",
      confidence: 0.88,
      gradient: "from-emerald-500 to-teal-600",
    },
    {
      id: 6,
      agent: "고객 경험 AI",
      icon: Bot,
      content:
        "사용자 인터페이스를 단순화하여 진입 장벽을 낮췄습니다. 온보딩 프로세스를 5단계에서 3단계로 줄였습니다.",
      round: 3,
      sentiment: "positive",
      confidence: 0.9,
      gradient: "from-cyan-500 to-blue-600",
    },
    {
      id: 7,
      agent: "운영 최적화 AI",
      icon: Network,
      content:
        "물류 네트워크 최적화로 배송 시간을 평균 2일 단축할 수 있습니다. 고객 만족도 향상이 기대됩니다.",
      round: 4,
      sentiment: "positive",
      confidence: 0.87,
      gradient: "from-violet-500 to-purple-600",
    },
    {
      id: 8,
      agent: "전략 기획 AI",
      icon: Sparkles,
      content:
        "모든 분석을 종합한 결과, 6개월 내 손익분기점 달성이 가능합니다. 단계적 시장 진입 전략을 추천합니다.",
      round: 4,
      sentiment: "positive",
      confidence: 0.93,
      gradient: "from-purple-600 to-pink-600",
    },
  ];

  useEffect(() => {
    if (currentMessageIndex < messages.length && !isRestarting) {
      const timer = setTimeout(() => {
        setDisplayedMessages((prev) => [
          ...prev,
          messages[currentMessageIndex],
        ]);
        setCurrentMessageIndex((prev) => prev + 1);
      }, 2000);

      return () => clearTimeout(timer);
    } else if (currentMessageIndex >= messages.length && !isRestarting) {
      setIsRestarting(true);
      const restartTimer = setTimeout(() => {
        setDisplayedMessages([]);
        setCurrentMessageIndex(0);
        setIsRestarting(false);
      }, 5000);

      return () => clearTimeout(restartTimer);
    }
  }, [currentMessageIndex, isRestarting]);

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case "positive":
        return <TrendingUp className="w-3 h-3" />;
      case "negative":
        return <TrendingDown className="w-3 h-3" />;
      case "neutral":
        return <Minus className="w-3 h-3" />;
      default:
        return <Minus className="w-3 h-3" />;
    }
  };

  const getSentimentStyles = (sentiment) => {
    switch (sentiment) {
      case "positive":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      case "negative":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      case "neutral":
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const getSentimentText = (sentiment) => {
    switch (sentiment) {
      case "positive":
        return "긍정적";
      case "negative":
        return "부정적";
      case "neutral":
        return "중립적";
      default:
        return "중립적";
    }
  };

  return (
    <div className="relative bg-black py-24">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-1/3 w-96 h-96 bg-purple-600 rounded-full filter blur-[128px] animate-pulse"></div>
        <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-pink-600 rounded-full filter blur-[128px] animate-pulse delay-700"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-lg border border-white/20 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Zap className="w-4 h-4 text-yellow-400" />
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              A2A Protocol Live Demo
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            가상 비즈니스 시뮬레이션
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            실시간으로 AI 에이전트들이 서로 소통하며 최적의 비즈니스 솔루션을
            도출하는 과정을 확인하세요
          </p>
        </div>

        {/* Chat Container */}
        <div className="relative">
          <div className="bg-gray-900/50 backdrop-blur-xl border border-gray-800 rounded-3xl overflow-hidden">
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 backdrop-blur-xl p-6 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 blur-lg opacity-50"></div>
                    <MessageSquare className="w-8 h-8 text-white relative z-10" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      AI 협업 회의실
                    </h3>
                    <p className="text-sm text-gray-400">
                      스마트팜 프로젝트 분석
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-gray-400">실시간 진행중</span>
                </div>
              </div>
            </div>

            {/* Messages Container */}
            <div className="h-[600px] overflow-y-auto p-6 space-y-4 custom-scrollbar">
              {displayedMessages.map((msg, index) => {
                const Icon = msg.icon;
                return (
                  <div
                    key={msg.id}
                    className="animate-fadeInUp"
                    style={{
                      animationDelay: `${index * 100}ms`,
                      animationFillMode: "both",
                    }}
                  >
                    <div className="flex gap-4">
                      {/* Agent Icon */}
                      <div
                        className={`w-12 h-12 bg-gradient-to-br ${msg.gradient} rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg`}
                      >
                        <Icon className="w-6 h-6 text-white" />
                      </div>

                      {/* Message Content */}
                      <div className="flex-1">
                        <div className="bg-gray-800/50 backdrop-blur-xl border border-gray-700 rounded-2xl p-4">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="font-semibold text-white">
                              {msg.agent}
                            </span>
                            <span className="text-xs text-gray-500">•</span>
                            <span className="text-xs text-gray-400">
                              라운드 {msg.round}
                            </span>
                            <span className="text-xs text-gray-500">•</span>
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 border ${getSentimentStyles(
                                msg.sentiment
                              )}`}
                            >
                              {getSentimentIcon(msg.sentiment)}
                              {getSentimentText(msg.sentiment)}
                            </span>
                            <span className="text-xs text-gray-500">•</span>
                            <span className="text-xs text-gray-400">
                              신뢰도: {(msg.confidence * 100).toFixed(0)}%
                            </span>
                          </div>
                          <p className="text-gray-300 leading-relaxed">
                            {msg.content}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Loading Indicator */}
              {currentMessageIndex < messages.length && !isRestarting && (
                <div className="flex justify-center py-4">
                  <div className="flex gap-1">
                    <div
                      className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
                      style={{ animationDelay: "0ms" }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
                      style={{ animationDelay: "150ms" }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
                      style={{ animationDelay: "300ms" }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Restart Message */}
              {isRestarting && (
                <div className="text-center py-8 animate-fadeIn">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full mb-4">
                    <RefreshCw className="w-8 h-8 text-white animate-spin" />
                  </div>
                  <p className="text-white font-semibold text-lg mb-2">
                    AI 협업 분석 완료
                  </p>
                  <p className="text-gray-400 text-sm">
                    모든 에이전트가 합의점에 도달했습니다
                  </p>
                  <p className="text-gray-500 text-xs mt-2">
                    5초 후 다시 시작됩니다...
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Info */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            이것은 실제 A2A Protocol의 시뮬레이션 데모입니다
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out;
        }

        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }

        /* Custom scrollbar */
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(31, 41, 55, 0.5);
          border-radius: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #9333ea, #ec4899);
          border-radius: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #7c3aed, #db2777);
        }
      `}</style>
    </div>
  );
}
